import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface BatchDetails {
  batchId: number;
  batchCode: string;
  sourceTypeId: number;
  sourceTypeName: string;
  vendorId: number;
  vendorName: string;
  subCategoryId: number;
  subCategoryName: string;
  productId: number;
  productName: string;
  manufactureDate: string;
  expiryDate: string;
  stock: number;
  availableStock: number;
  allocatedStock: number;
  availableStockForSubCategory:number;
}

interface InwardDialogProps {
  visible: boolean;
  productId: number | null;
  posId: number | null;
  productName: string;
  posName: string;
  onHide: () => void;
  onSuccess: () => void;
  minOrderQty?: number;
}

const InwardDialog: React.FC<InwardDialogProps> = ({
  visible,
  productId,
  posId,
  productName,
  posName,
  onHide,
  onSuccess,
  minOrderQty,
}) => {
  const toast = useRef<Toast>(null);
  const [batches, setBatches] = useState<BatchDetails[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<BatchDetails | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [note, setNote] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [batchError, setBatchError] = useState<string>("");
  const allowDecimals = minOrderQty ? minOrderQty % 1 !== 0 : false;

  const formatDateOnly = (dateString: string): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchBatches = async () => {
    if (!productId || !posId) return;

    try {
      setLoading(true);
      setBatchError("");
      const response = await posService.getbatchlistforproduct(productId,posId);

      if (response && Array.isArray(response)) {
        const availableBatches = response.filter(
          (batch: BatchDetails) => batch.availableStock > 0
        );
        setBatches(availableBatches);
        if (availableBatches.length === 0) {
          const message = "No batches available for this product";
          setBatchError(message);
          showMessage("Validation Error", message, "warn");
        }
      } else {
        setBatches([]);
        const message = "No batches found for this product";
        setBatchError(message);
        showMessage("Validation Error", message, "warn");
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      const message = "Failed to load batches";
      setBatchError(message);
      showMessage("Error", message, "error");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && productId && posId) {
      // Reset form
      setSelectedBatch(null);
      setQuantity(null);
      setNote("");
      setBatchError("");
      fetchBatches();
    }
  }, [visible, productId, posId]);

  const handleSave = async () => {
    // Validation
    if (batches.length === 0) {
      const message = batchError || "No batches available for this product";
      showMessage("Validation Error", message, "warn");
      return;
    }

    if (!selectedBatch) {
      showMessage("Validation Error", "Please select a batch", "warn");
      return;
    }

    if (!quantity || quantity <= 0) {
      showMessage("Validation Error", "Quantity must be greater than 0", "warn");
      return;
    }

    if (quantity > selectedBatch.availableStockForSubCategory) {
        debugger
      showMessage(
        "Validation Error",
        `Quantity cannot exceed available stock (${selectedBatch.availableStockForSubCategory})`,
        "warn"
      );
      return;
    }

    if (!posId) {
      showMessage("Error", "POS ID is missing", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        targetPosId: posId,
        productId: productId,
        batchId: selectedBatch.batchId,
        quantityToAdd: quantity!,
        note: note || "",
      };

      const response = await posService.addStockFromBatch(payload);

      if (response?.isSuccess) {
        showMessage("Success", response.message || "Stock added successfully", "success");
        onSuccess();
        onHide();
      } else {
        showMessage("Error", response?.message || "Failed to add stock", "error");
      }
    } catch (error) {
      console.error("Error adding stock:", error);
      showMessage("Error", "An error occurred while adding stock", "error");
    } finally {
      setLoading(false);
    }
  };

  const batchOptionTemplate = (option: BatchDetails) => {
    return (
      <div className="py-2 px-1 text-sm">
        <div className="font-medium text-gray-900">{option.batchCode}</div>
        <div className="text-gray-600 text-xs mt-1">
          {option.availableStock} available • Exp: {formatDateOnly(option.expiryDate)}
        </div>
      </div>
    );
  };

  const selectedBatchTemplate = (option: BatchDetails | null) => {
    if (!option) return <span className="text-gray-500">Select batch</span>;
    return (
      <div className="text-sm">
        <span className="font-medium text-gray-900">{option.batchCode}</span>
        <span className="text-gray-600 ml-2">({option.availableStock} avail)</span>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Inward Stock"
        modal
        className="p-fluid"
        style={{ width: "40vw" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
      >
        <div>
          {/* Product Name and POS */}
          {/* <div className="field mb-3">
            <label htmlFor="productPos" className="font-semibold text-sm block mb-1">
              Product | POS
            </label>
            <InputText
              id="productPos"
              disabled
              value={`${productName} | ${posName}`}
              className="w-full"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div> */}
          {/* Product & POS in a single row */}
        <div className="field mb-3">
        <label className="font-semibold text-sm block mb-1">Product & POS</label>

        <div className="flex gap-2">
            {/* POS (30%) */}
            <div style={{ flex: "0 0 20%" }}>
            <InputText
                value={posName}
                disabled
                className="w-full"
                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            </div>

            {/* Product Name (70%) */}
            <div style={{ flex: "1 0 80%" }}>
            <InputText
                value={productName}
                disabled
                className="w-full"
                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            </div>
        </div>
        </div>


          {/* Batch Selection */}
          <div className="field mb-3">
            <label htmlFor="batch" className="font-semibold text-sm block mb-1">
              Select Batch <span className="text-red-500">*</span>
            </label>
            <Dropdown
              id="batch"
              value={selectedBatch}
              options={batches}
              onChange={(e) => setSelectedBatch(e.value)}
              optionLabel="batchCode"
              placeholder="Select a batch"
              filter
              filterBy="batchCode"
              itemTemplate={batchOptionTemplate}
              valueTemplate={selectedBatchTemplate}
              className="w-full"
              disabled={loading || batches.length === 0}
              emptyMessage="No batches available"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {batchError && (
              <small className="text-red-500 block mt-2">{batchError}</small>
            )}
            {selectedBatch && (
              <small className="text-gray-600 block mt-2">
                Expires: <span className="font-semibold text-gray-900">{formatDateOnly(selectedBatch.expiryDate)}</span>
              </small>
            )}
          </div>

          {/* Quantity */}
          <div className="field mb-3">
            <label htmlFor="quantity" className="font-semibold text-sm block mb-1">
              Quantity <span className="text-red-500">*</span>
            </label>
            <InputNumber
              id="quantity"
              value={quantity}
              onValueChange={(e) => setQuantity(e.value ?? null)}
              placeholder="Enter quantity"
              min={0}
            //   max={selectedBatch?.availableStock || undefined}
              minFractionDigits={0} 
              maxFractionDigits={allowDecimals ? 3 : 0} 
              useGrouping={false}
              className="w-full"
              disabled={!selectedBatch}
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {selectedBatch && (
              <small className="text-gray-600 block mt-1">
                Maximum available: {selectedBatch.availableStockForSubCategory}
              </small>
            )}
          </div>

          {/* Note */}
          <div className="field mb-3">
            <label htmlFor="note" className="font-semibold text-sm block mb-1">
              Note
            </label>
            <InputText
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter note (optional)"
              className="w-full"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              onClick={onHide}
              severity="secondary"
              className="text-black border-round p-button-m"
              disabled={loading}
            />
            <Button
              label="Save"
              onClick={handleSave}
              loading={loading}
              // disabled={loading || !selectedBatch || quantity <= 0}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default InwardDialog;

